import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import AppShell from "../components/AppShell";
import StatCard from "../components/StatCard";
import StatusBadge from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { formatDateTime, getErrorMessage } from "../lib/formatters";


const navItems = [
  { path: "/platform/dashboard", label: "Platform Dashboard", shortLabel: "HOME", testId: "platform-dashboard" },
  { path: "/platform/tenants", label: "Tenant Management", shortLabel: "ORG", testId: "platform-tenants" },
  { path: "/platform/audit", label: "Audit Logs", shortLabel: "LOGS", testId: "platform-audit" },
];


const planOptions = ["Essentials", "Growth", "Scale"];


export default function SuperAdminPage({ section }) {
  const { user, tenant, logout } = useAuth();
  const [dashboard, setDashboard] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [tenantDrafts, setTenantDrafts] = useState({});
  const [latestOnboarding, setLatestOnboarding] = useState(null);
  const [createForm, setCreateForm] = useState({
    name: "",
    code: "",
    pricing_plan: "Growth",
    web_portal_attendance: true,
    kiosk_qr_attendance: false,
    employee_qr_scan_attendance: false,
    photo_scanner_attendance: false,
    create_company_admin: true,
    company_admin_name: "",
    company_admin_email: "",
    company_admin_phone: "",
    send_company_admin_email: false,
  });
  const [loading, setLoading] = useState(true);

  const pageMeta = useMemo(
    () => ({
      dashboard: {
        title: "Platform command centre",
        subtitle: "Monitor tenant adoption, attendance method pricing, and recent platform actions.",
      },
      tenants: {
        title: "Manage tenant workspaces",
        subtitle: "Create companies, adjust pricing plans, and control attendance feature access per tenant.",
      },
      audit: {
        title: "Platform audit trail",
        subtitle: "Review every platform-level action taken by the Super Admin layer.",
      },
    }),
    [],
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const requests = [api.get("/super-admin/tenants")];
      if (section === "dashboard") requests.unshift(api.get("/super-admin/dashboard"));
      if (section === "audit") requests.push(api.get("/super-admin/audit-logs"));
      const responses = await Promise.all(requests);
      if (section === "dashboard") {
        setDashboard(responses[0].data);
        setTenants(responses[1].data);
      } else if (section === "audit") {
        setTenants(responses[0].data);
        setAuditLogs(responses[1].data);
      } else {
        setTenants(responses[0].data);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [section]);

  useEffect(() => {
    const nextDrafts = {};
    tenants.forEach((tenantRow) => {
      nextDrafts[tenantRow.id] = {
        pricing_plan: tenantRow.pricing_plan,
        active: tenantRow.active,
        company_admin_name: tenantRow.company_admin?.name || "",
        company_admin_email: tenantRow.company_admin?.email || "",
        company_admin_phone: tenantRow.company_admin?.phone || "",
        company_admin_active: tenantRow.company_admin?.active ?? true,
      };
    });
    setTenantDrafts(nextDrafts);
  }, [tenants]);

  const updateTenant = async (tenantId, payload) => {
    try {
      const response = await api.put(`/super-admin/tenants/${tenantId}`, payload);
      if (response.data.company_admin_credentials) {
        setLatestOnboarding({
          tenant: response.data.tenant,
          company_admin_credentials: response.data.company_admin_credentials,
        });
        toast.success("Company Admin updated. Share the new temporary password manually.");
      } else {
        toast.success("Tenant updated");
      }
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const toggleFlag = async (tenantRow, key) => {
    try {
      await api.put(`/super-admin/tenants/${tenantRow.id}/feature-flags`, {
        ...tenantRow.feature_flags,
        [key]: !tenantRow.feature_flags?.[key],
      });
      toast.success("Feature flags updated");
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleCreateTenant = async (event) => {
    event.preventDefault();
    try {
      const response = await api.post("/super-admin/tenants", createForm);
      setLatestOnboarding(response.data);
      toast.success(response.data.company_admin_credentials ? "Tenant created. Share the company admin credentials manually." : "Tenant created successfully");
      setCreateForm({
        name: "",
        code: "",
        pricing_plan: "Growth",
        web_portal_attendance: true,
        kiosk_qr_attendance: false,
        employee_qr_scan_attendance: false,
        photo_scanner_attendance: false,
        create_company_admin: true,
        company_admin_name: "",
        company_admin_email: "",
        company_admin_phone: "",
        send_company_admin_email: false,
      });
      loadData();
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const copyValue = async (label, value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch {
      toast.error(`Could not copy ${label.toLowerCase()}`);
    }
  };

  const renderDashboard = () => (
    <>
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5" data-testid="super-admin-stats-grid">
        <StatCard label="Total Tenants" value={dashboard?.stats?.total_tenants || 0} accent="#0D1B2A" helper="All onboarded workspaces" testId="platform-stat-total-tenants" />
        <StatCard label="Active Tenants" value={dashboard?.stats?.active_tenants || 0} accent="#F4A300" helper="Currently enabled companies" testId="platform-stat-active-tenants" />
        <StatCard label="Kiosk Enabled" value={dashboard?.stats?.kiosk_enabled || 0} accent="#F59E0B" helper="Tenants using shared QR kiosks" testId="platform-stat-kiosk-enabled" />
        <StatCard label="Employee QR Enabled" value={dashboard?.stats?.employee_qr_enabled || 0} accent="#0D1B2A" helper="Tenants with employee QR scan access" testId="platform-stat-employee-qr-enabled" />
        <StatCard label="Photo Scanner Enabled" value={dashboard?.stats?.photo_scanner_enabled || 0} accent="#F4A300" helper="Tenants using browser camera attendance" testId="platform-stat-photo-scanner-enabled" />
      </div>

      <Card className="border-slate-200/70 bg-white/85" data-testid="platform-recent-audit-card">
        <CardHeader>
          <CardTitle data-testid="platform-recent-audit-title">Recent platform actions</CardTitle>
          <CardDescription data-testid="platform-recent-audit-description">Super Admin actions are tracked separately from tenant activity.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="platform-recent-audit-table-wrap">
            <Table data-testid="platform-recent-audit-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead>Details</TableHead>
                  <TableHead>Time</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(dashboard?.recent_audit_logs || []).map((log) => (
                  <TableRow key={log.id} data-testid={`platform-audit-row-${log.id}`}>
                    <TableCell className="font-semibold text-slate-800">{log.action}</TableCell>
                    <TableCell>{log.entity_type}</TableCell>
                    <TableCell>{log.details}</TableCell>
                    <TableCell>{formatDateTime(log.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderTenants = () => (
    <>
      <Card className="border-slate-200/70 bg-white/85" data-testid="tenant-create-card">
        <CardHeader>
          <CardTitle data-testid="tenant-create-title">Create a new tenant</CardTitle>
          <CardDescription data-testid="tenant-create-description">Set pricing, create the first Company Admin, and manually share the generated temporary password.</CardDescription>
        </CardHeader>
        <CardContent>
          <form className="grid gap-4 lg:grid-cols-2" onSubmit={handleCreateTenant} data-testid="tenant-create-form">
            <Input placeholder="Company name" value={createForm.name} onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))} data-testid="tenant-create-name-input" />
            <Input placeholder="Tenant code" value={createForm.code} onChange={(event) => setCreateForm((current) => ({ ...current, code: event.target.value.toUpperCase() }))} data-testid="tenant-create-code-input" />
            <Input placeholder="Company Admin full name" value={createForm.company_admin_name} onChange={(event) => setCreateForm((current) => ({ ...current, company_admin_name: event.target.value }))} data-testid="tenant-create-admin-name-input" />
            <Input type="email" placeholder="Company Admin email" value={createForm.company_admin_email} onChange={(event) => setCreateForm((current) => ({ ...current, company_admin_email: event.target.value.toLowerCase() }))} data-testid="tenant-create-admin-email-input" />
            <select className="h-11 rounded-2xl border border-slate-200 px-3 text-sm" value={createForm.pricing_plan} onChange={(event) => setCreateForm((current) => ({ ...current, pricing_plan: event.target.value }))} data-testid="tenant-create-plan-select">
              {planOptions.map((plan) => <option key={plan}>{plan}</option>)}
            </select>
            <Input placeholder="Company Admin phone (optional)" value={createForm.company_admin_phone} onChange={(event) => setCreateForm((current) => ({ ...current, company_admin_phone: event.target.value }))} data-testid="tenant-create-admin-phone-input" />
            <div className="flex flex-wrap gap-3" data-testid="tenant-create-flags-group">
              {[
                ["web_portal_attendance", "Web portal"],
                ["kiosk_qr_attendance", "Kiosk QR"],
                ["employee_qr_scan_attendance", "Employee QR"],
                ["photo_scanner_attendance", "Photo Scanner"],
              ].map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setCreateForm((current) => ({ ...current, [key]: !current[key] }))}
                  className={`rounded-full border px-4 py-2 text-sm font-semibold ${createForm[key] ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-white text-slate-500"}`}
                  data-testid={`tenant-create-${key}-toggle`}
                >
                  {label}: {createForm[key] ? "ON" : "OFF"}
                </button>
              ))}
            </div>
            <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-500" data-testid="tenant-create-manual-share-note">
              The first Company Admin will be created with a temporary password and forced reset on first login. Email sending stays off for now, so share the credentials manually.
            </div>
            <Button type="submit" className="rounded-2xl bg-[#F4A300] text-[#0D1B2A] hover:bg-[#D89000] lg:col-span-2" data-testid="tenant-create-submit-button">Create tenant & company admin</Button>
          </form>

          {latestOnboarding?.company_admin_credentials ? (
            <div className="mt-6 rounded-[28px] border border-emerald-200 bg-emerald-50/80 p-5" data-testid="tenant-onboarding-result-card">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.26em] text-emerald-700" data-testid="tenant-onboarding-result-label">Manual sharing bundle</p>
                  <h3 className="mt-2 text-2xl font-extrabold text-slate-900" data-testid="tenant-onboarding-result-title">{latestOnboarding.tenant.name} is ready</h3>
                  <p className="mt-2 text-sm text-slate-600" data-testid="tenant-onboarding-result-copy">Share these credentials with the new Company Admin. They will be forced to reset the password on first login.</p>
                </div>
                <div className="flex flex-wrap gap-2" data-testid="tenant-onboarding-result-actions">
                  <Button type="button" variant="outline" className="rounded-2xl" onClick={() => copyValue("login email", latestOnboarding.company_admin_credentials.email)} data-testid="tenant-onboarding-copy-email-button">Copy email</Button>
                  <Button type="button" variant="outline" className="rounded-2xl" onClick={() => copyValue("temporary password", latestOnboarding.company_admin_credentials.temporary_password)} data-testid="tenant-onboarding-copy-password-button">Copy password</Button>
                  <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => copyValue("credential bundle", `Tenant: ${latestOnboarding.tenant.name}\nEmail: ${latestOnboarding.company_admin_credentials.email}\nTemporary Password: ${latestOnboarding.company_admin_credentials.temporary_password}`)} data-testid="tenant-onboarding-copy-bundle-button">Copy all</Button>
                </div>
              </div>

              <div className="mt-5 grid gap-3 md:grid-cols-3" data-testid="tenant-onboarding-result-grid">
                <div className="rounded-2xl border border-emerald-200 bg-white p-4" data-testid="tenant-onboarding-email-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Login email</p>
                  <p className="mt-2 break-all font-semibold text-slate-900" data-testid="tenant-onboarding-email-value">{latestOnboarding.company_admin_credentials.email}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-white p-4" data-testid="tenant-onboarding-password-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">Temporary password</p>
                  <p className="mt-2 break-all font-semibold text-slate-900" data-testid="tenant-onboarding-password-value">{latestOnboarding.company_admin_credentials.temporary_password}</p>
                </div>
                <div className="rounded-2xl border border-emerald-200 bg-white p-4" data-testid="tenant-onboarding-reset-card">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-400">First login rule</p>
                  <p className="mt-2 font-semibold text-slate-900" data-testid="tenant-onboarding-reset-value">Forced password reset</p>
                </div>
              </div>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <Card className="border-slate-200/70 bg-white/85" data-testid="tenant-list-card">
        <CardHeader>
          <CardTitle data-testid="tenant-list-title">Tenant directory</CardTitle>
          <CardDescription data-testid="tenant-list-description">Pricing, activation state, and attendance feature gates stay under platform control.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="tenant-list-table-wrap">
            <Table data-testid="tenant-list-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Tenant</TableHead>
                  <TableHead>Employees</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Company Admin</TableHead>
                  <TableHead>Feature Flags</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tenants.map((tenantRow) => (
                  <TableRow key={tenantRow.id} data-testid={`tenant-row-${tenantRow.id}`}>
                    <TableCell>
                      <div>
                        <p className="font-semibold text-slate-900" data-testid={`tenant-name-${tenantRow.id}`}>{tenantRow.name}</p>
                        <p className="text-xs text-slate-500" data-testid={`tenant-code-${tenantRow.id}`}>{tenantRow.code}</p>
                        <p className="mt-1 text-xs text-slate-500" data-testid={`tenant-company-admin-${tenantRow.id}`}>{tenantRow.company_admin?.email || "Company Admin not added yet"}</p>
                      </div>
                    </TableCell>
                    <TableCell data-testid={`tenant-employees-${tenantRow.id}`}>{tenantRow.employee_count}</TableCell>
                    <TableCell>
                      <select
                        className="h-10 rounded-2xl border border-slate-200 px-3 text-sm"
                        value={tenantDrafts[tenantRow.id]?.pricing_plan || tenantRow.pricing_plan}
                        onChange={(event) => setTenantDrafts((current) => ({ ...current, [tenantRow.id]: { ...current[tenantRow.id], pricing_plan: event.target.value } }))}
                        data-testid={`tenant-plan-select-${tenantRow.id}`}
                      >
                        {planOptions.map((plan) => <option key={plan}>{plan}</option>)}
                      </select>
                    </TableCell>
                    <TableCell><StatusBadge value={tenantRow.active ? "Present" : "Absent"} testId={`tenant-status-${tenantRow.id}`} /></TableCell>
                    <TableCell>
                      <div className="grid gap-2" data-testid={`tenant-admin-edit-wrap-${tenantRow.id}`}>
                        <Input
                          value={tenantDrafts[tenantRow.id]?.company_admin_name || ""}
                          onChange={(event) => setTenantDrafts((current) => ({ ...current, [tenantRow.id]: { ...current[tenantRow.id], company_admin_name: event.target.value } }))}
                          placeholder="Admin name"
                          data-testid={`tenant-admin-name-input-${tenantRow.id}`}
                        />
                        <Input
                          type="email"
                          value={tenantDrafts[tenantRow.id]?.company_admin_email || ""}
                          onChange={(event) => setTenantDrafts((current) => ({ ...current, [tenantRow.id]: { ...current[tenantRow.id], company_admin_email: event.target.value.toLowerCase() } }))}
                          placeholder="Admin email"
                          data-testid={`tenant-admin-email-input-${tenantRow.id}`}
                        />
                        <Input
                          value={tenantDrafts[tenantRow.id]?.company_admin_phone || ""}
                          onChange={(event) => setTenantDrafts((current) => ({ ...current, [tenantRow.id]: { ...current[tenantRow.id], company_admin_phone: event.target.value } }))}
                          placeholder="Admin phone"
                          data-testid={`tenant-admin-phone-input-${tenantRow.id}`}
                        />
                        <button
                          type="button"
                          className={`rounded-full border px-3 py-2 text-xs font-semibold ${tenantDrafts[tenantRow.id]?.company_admin_active ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-slate-100 text-slate-500"}`}
                          onClick={() => setTenantDrafts((current) => ({ ...current, [tenantRow.id]: { ...current[tenantRow.id], company_admin_active: !current[tenantRow.id]?.company_admin_active } }))}
                          data-testid={`tenant-admin-active-toggle-${tenantRow.id}`}
                        >
                          Admin {tenantDrafts[tenantRow.id]?.company_admin_active ? "Active" : "Inactive"}
                        </button>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2" data-testid={`tenant-flags-${tenantRow.id}`}>
                        {[
                          ["web_portal_attendance", "Web"],
                          ["kiosk_qr_attendance", "Kiosk"],
                          ["employee_qr_scan_attendance", "Employee QR"],
                          ["photo_scanner_attendance", "Photo Scanner"],
                        ].map(([key, label]) => (
                          <button
                            type="button"
                            key={key}
                            className={`rounded-full border px-3 py-1 text-xs font-semibold ${tenantRow.feature_flags?.[key] ? "border-[#F4A300]/25 bg-[#F4A300]/10 text-[#C27D00]" : "border-slate-200 bg-slate-100 text-slate-500"}`}
                            onClick={() => toggleFlag(tenantRow, key)}
                            data-testid={`tenant-flag-${tenantRow.id}-${key}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-2" data-testid={`tenant-actions-${tenantRow.id}`}>
                        <Button type="button" variant="outline" className="rounded-2xl" onClick={() => updateTenant(tenantRow.id, { pricing_plan: tenantDrafts[tenantRow.id]?.pricing_plan || tenantRow.pricing_plan })} data-testid={`tenant-save-${tenantRow.id}`}>
                          Save Plan
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="rounded-2xl"
                          onClick={() => updateTenant(tenantRow.id, {
                            company_admin_name: tenantDrafts[tenantRow.id]?.company_admin_name || "",
                            company_admin_email: tenantDrafts[tenantRow.id]?.company_admin_email || "",
                            company_admin_phone: tenantDrafts[tenantRow.id]?.company_admin_phone || "",
                            company_admin_active: tenantDrafts[tenantRow.id]?.company_admin_active ?? true,
                          })}
                          data-testid={`tenant-save-admin-${tenantRow.id}`}
                        >
                          Save Admin
                        </Button>
                        <Button type="button" className="rounded-2xl bg-[#0D1B2A] text-white hover:bg-[#08131F]" onClick={() => updateTenant(tenantRow.id, { active: !tenantRow.active })} data-testid={`tenant-toggle-active-${tenantRow.id}`}>
                          {tenantRow.active ? "Disable" : "Enable"}
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </>
  );

  const renderAudit = () => (
    <Card className="border-slate-200/70 bg-white/85" data-testid="platform-audit-card">
      <CardHeader>
        <CardTitle data-testid="platform-audit-title">Platform audit history</CardTitle>
        <CardDescription data-testid="platform-audit-description">Every Super Admin action is captured here for governance and visibility.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-hidden rounded-3xl border border-slate-200/70" data-testid="platform-audit-table-wrap">
          <Table data-testid="platform-audit-table">
            <TableHeader>
              <TableRow>
                <TableHead>Action</TableHead>
                <TableHead>Actor</TableHead>
                <TableHead>Entity</TableHead>
                <TableHead>Details</TableHead>
                <TableHead>Time</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {auditLogs.map((log) => (
                <TableRow key={log.id} data-testid={`platform-audit-log-row-${log.id}`}>
                  <TableCell className="font-semibold text-slate-900">{log.action}</TableCell>
                  <TableCell>{log.actor_role}</TableCell>
                  <TableCell>{log.entity_type}</TableCell>
                  <TableCell>{log.details}</TableCell>
                  <TableCell>{formatDateTime(log.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <AppShell title={pageMeta[section].title} subtitle={pageMeta[section].subtitle} navItems={navItems} user={user} tenant={tenant} onLogout={logout}>
      {loading ? <Card className="border-slate-200/70 bg-white/85 p-10 text-center text-slate-500" data-testid="platform-loading-card">Loading platform data...</Card> : null}
      {!loading && section === "dashboard" ? renderDashboard() : null}
      {!loading && section === "tenants" ? renderTenants() : null}
      {!loading && section === "audit" ? renderAudit() : null}
    </AppShell>
  );
}